import React, { useState, useMemo } from 'react';
import { X, Sandwich, Egg, Soup, UtensilsCrossed, Check, AlertCircle, Search } from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { getRecipeSuggestions } from '../../../utils/recipeMatching';

/**
 * Effort levels for filtering
 */
const EFFORT_LEVELS = [
  { id: 'no-cook', icon: Sandwich, name: 'No cook' },
  { id: 'one-pan', icon: Egg, name: 'One pan' },
  { id: 'some-steps', icon: Soup, name: 'Some steps' },
  { id: 'full-cook', icon: UtensilsCrossed, name: 'Full cook' }
];

/**
 * Availability filter options
 */
const AVAILABILITY_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'almost', label: 'Almost' }
];

/**
 * Protein/category filter options
 */
const PROTEIN_OPTIONS = [
  { id: 'all', label: 'Any' },
  { id: 'chicken', label: 'Chicken' },
  { id: 'beef', label: 'Beef' },
  { id: 'pork', label: 'Pork' },
  { id: 'fish', label: 'Fish' },
  { id: 'egg', label: 'Egg' },
  { id: 'tofu', label: 'Tofu' },
  { id: 'vegetarian', label: 'Veggie' }
];

/**
 * Check if recipe contains a protein type
 */
function recipeHasProtein(recipe, proteinFilter) {
  if (proteinFilter === 'all') return true;
  
  const allIngredients = [...(recipe.needs || []), ...(recipe.optional || [])];
  const ingredientsLower = allIngredients.map(i => i.toLowerCase());
  
  if (proteinFilter === 'vegetarian') {
    // Vegetarian = no meat/fish
    const meats = ['chicken', 'beef', 'pork', 'lamb', 'bacon', 'ham', 'sausage', 'meat', 'fish', 'salmon', 'tuna', 'shrimp', 'prawn'];
    return !ingredientsLower.some(ing => meats.some(m => ing.includes(m)));
  }
  
  // Check if recipe needs or optionally uses this protein
  return ingredientsLower.some(ing => ing.includes(proteinFilter));
}

/**
 * Get default effort based on mood
 */
function getDefaultEffort(mood) {
  switch (mood) {
    case 'Rough':
    case 'Meh':
      return 'no-cook';
    case 'Okay':
      return 'one-pan';
    case 'Good':
      return 'some-steps';
    case 'Great':
      return 'full-cook';
    default:
      return 'one-pan';
  }
}

/**
 * Format date for display
 */
function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
}

/**
 * MealPicker - Modal for selecting a meal for a specific day
 * 
 * Features:
 * - Search by recipe name
 * - Filter by effort level
 * - Filter by availability (ready/almost/all)
 * - Filter by protein type
 */
export default function MealPicker({
  stockedItems = [],
  currentState = {},
  onSelect,
  onClose,
  selectedDate
}) {
  const [effortLevel, setEffortLevel] = useState(() => getDefaultEffort(currentState.mood));
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [proteinFilter, setProteinFilter] = useState('all');

  // Get recipe matches based on pantry and effort
  const recipeMatches = useMemo(() => {
    return getRecipeSuggestions(stockedItems, effortLevel);
  }, [stockedItems, effortLevel]);

  // Apply all filters
  const filteredRecipes = useMemo(() => {
    let recipes = [...recipeMatches.canMake, ...recipeMatches.almostCanMake];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      recipes = recipes.filter(r => 
        r.name.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }
    
    // Availability filter
    if (availabilityFilter === 'ready') {
      recipes = recipes.filter(r => r.canMake);
    } else if (availabilityFilter === 'almost') {
      recipes = recipes.filter(r => !r.canMake);
    }
    
    // Protein filter
    if (proteinFilter !== 'all') {
      recipes = recipes.filter(r => recipeHasProtein(r, proteinFilter));
    }
    
    return recipes;
  }, [recipeMatches, searchQuery, availabilityFilter, proteinFilter]);

  // Count for filter badges
  const readyCount = recipeMatches.canMake.length;
  const almostCount = recipeMatches.almostCanMake.length;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      zIndex: 1100
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px 16px 0 0',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              Pick a meal
            </h2>
            <p style={{
              margin: '4px 0 0',
              fontSize: '13px',
              color: ABLE_COLORS.textMuted
            }}>
              {formatDateFull(selectedDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: ABLE_COLORS.textMuted,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: ABLE_COLORS.bgSubtle,
            borderRadius: '8px',
            padding: '8px 12px'
          }}>
            <Search size={18} color={ABLE_COLORS.textMuted} />
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                fontSize: '15px',
                outline: 'none',
                color: ABLE_COLORS.textPrimary
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: ABLE_COLORS.textMuted,
                  display: 'flex'
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Effort Filter */}
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            {EFFORT_LEVELS.map((level) => {
              const isSelected = effortLevel === level.id;
              const IconComponent = level.icon;
              return (
                <button
                  key={level.id}
                  onClick={() => setEffortLevel(level.id)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    background: isSelected ? `${ABLE_COLORS.primary}10` : ABLE_COLORS.bgSubtle,
                    border: `2px solid ${isSelected ? ABLE_COLORS.primary : 'transparent'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <IconComponent 
                    size={18} 
                    color={isSelected ? ABLE_COLORS.primary : ABLE_COLORS.textMuted}
                  />
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary
                  }}>
                    {level.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Availability + Protein Filters */}
        <div style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
          flexShrink: 0,
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          {/* Availability Filter */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {AVAILABILITY_OPTIONS.map((opt) => {
              const isSelected = availabilityFilter === opt.id;
              const count = opt.id === 'ready' ? readyCount : opt.id === 'almost' ? almostCount : null;
              return (
                <button
                  key={opt.id}
                  onClick={() => setAvailabilityFilter(opt.id)}
                  style={{
                    padding: '5px 10px',
                    background: isSelected ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
                    color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                    border: 'none',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {opt.label}
                  {count !== null && (
                    <span style={{
                      background: isSelected ? 'rgba(255,255,255,0.3)' : ABLE_COLORS.border,
                      padding: '1px 5px',
                      borderRadius: '8px',
                      fontSize: '10px'
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Protein Filter */}
          <div style={{ 
            display: 'flex', 
            gap: '6px', 
            alignItems: 'center',
            overflowX: 'auto',
            flex: 1,
            minWidth: 0
          }}>
            {PROTEIN_OPTIONS.map((opt) => {
              const isSelected = proteinFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setProteinFilter(opt.id)}
                  style={{
                    padding: '5px 10px',
                    background: isSelected ? ABLE_COLORS.warm : ABLE_COLORS.bgSubtle,
                    color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                    border: 'none',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Recipe List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px'
        }}>
          {filteredRecipes.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: ABLE_COLORS.textMuted
            }}>
              <p style={{ margin: 0 }}>No recipes match your filters.</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>
                Try adjusting effort level, availability, or protein type.
              </p>
            </div>
          )}

          {filteredRecipes.map(recipe => (
            <button
              key={recipe.id}
              onClick={() => onSelect(recipe)}
              style={{
                width: '100%',
                padding: '14px',
                marginBottom: '8px',
                background: 'white',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: '6px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: '600',
                  color: ABLE_COLORS.textPrimary
                }}>
                  {recipe.name}
                </h3>
                
                {recipe.canMake && (
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    color: ABLE_COLORS.success,
                    background: `${ABLE_COLORS.success}15`,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    flexShrink: 0
                  }}>
                    <Check size={12} />
                    Ready
                  </span>
                )}
              </div>

              <p style={{
                margin: '0 0 8px',
                fontSize: '13px',
                color: ABLE_COLORS.textSecondary,
                lineHeight: 1.4
              }}>
                {recipe.description}
              </p>

              {/* Ingredients status */}
              <div style={{ fontSize: '12px' }}>
                {recipe.hasIngredients?.length > 0 && (
                  <div style={{
                    color: ABLE_COLORS.success,
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '4px'
                  }}>
                    <Check size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>Have: {recipe.hasIngredients.slice(0, 4).join(', ')}{recipe.hasIngredients.length > 4 ? '...' : ''}</span>
                  </div>
                )}
                
                {recipe.missingRequired?.length > 0 && (
                  <div style={{
                    color: ABLE_COLORS.coral,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '4px'
                  }}>
                    <AlertCircle size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>Need: {recipe.missingRequired.join(', ')}</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
